<template>
  <div class="text-left relative">

    <div
      class="bg-light-100 dark:bg-dark-800 rounded-lg px-4 h-[3.25rem] flex items-center font-medium text-md"
      :class="[
        modelValue?.name || initialValue ? 'text-light-950 dark:text-dark-50' : 'text-light-600 dark:text-dark-400'
      ]"
      @click="() => isPopinActive = true"
    >
      <p class="grow">{{ modelValue?.name ?? (initialValue ? initialValue : placeholder) }}</p>
      <IconBase name="cute/pen-fill" :width="16" class="text-light-600 dark:text-dark-50" v-if="modelValue?.name || initialValue" />
      <IconBase name="cute/search-fill" :width="16" class="text-light-600 dark:text-dark-50" v-else />
    </div>

    <PopinBase :is-active="isPopinActive" size="2xs" @close="() => { isPopinActive = false; search = '' }" :z="280">
      <template #body>
        <div class="flex flex-col h-full">
          <div class="p-3 grow space-y-1 overflow-auto bg-white dark:bg-charcoal-950">
            <div
              class="p-1.5 text-base hover:bg-light-100 hover:dark:bg-dark-900  font-medium leading-snug rounded-lg cursor-pointer flex items-center"
              v-for="suggestion in suggestions"
              @click="() => onSelect(suggestion)"
              v-if="suggestions.length > 0"
            >
              <div class="size-10 bg-light-300 dark:bg-dark-600 rounded-lg shrink-0 mr-2.5 flex items-center justify-center text-light-900 dark:text-dark-100 text-center">
                <IconBase :name="`countries/${suggestion.context?.country?.country_code}`" :width="20" v-if="suggestion.context?.country?.country_code" />
                <IconBase :name="'maki/' + (suggestion.maki ?? 'marker')" :width="20" v-else />
              </div>
              
              <div class="grow">
                <p class="line-clamp-1">{{ suggestion.name }}</p>
                <p class="line-clamp-1 text-sm text-light-800 dark:text-dark-400">
                  <!-- <IconBase :name="`countries/${suggestion.context?.country?.country_code}`" :width="16" class="-mt-0.5 mr-0.5" v-if="suggestion.context?.country?.country_code" /> -->
                  {{ suggestion.full_address ? suggestion.full_address : suggestion.context?.country?.name }}
                </p>
              </div>
            </div>
            <div class="text-center p-6" v-else-if="isLoading">
              <IconBase name="cute/spinner" class="spin" />
            </div>
            <div class="bg-charcoal-100 rounded-xl dark:bg-dark-800 text-sm font-medium text-charcoal-600 py-6 text-center" v-else-if="suggestions.length === 0">
              {{ $t('components.InputAddress.empty') }}
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="p-4 border-t border-charcoal-200 dark:border-charcoal-800 shrink-0">
            <InputBase v-bind="props" :placeholder="placeholder ? placeholder : $t('components.InputAddress.placeholder')" :is-disabled="isDisabled" v-model="search" ref="$popinInput" is-no-autocomplete />
          </div>
      </template>
    </PopinBase>
  </div>
</template>

<script lang="ts" setup>
import { Address } from '@/classes'
import { SearchBoxSuggestion } from '@mapbox/search-js-core'
import { placeAutocompleteAPI, placeAutocompleteGetAPI } from '@/api/places'

const props = withDefaults(defineProps<{
  id?: string
  modelValue: Address | null
  isRequired?: boolean
  isDisabled?: boolean
  label?: string
  placeholder?: string
  isWorld?: boolean
  initialValue?: string
  types?: 'cities'
}>(), {
  id: Math.random().toString(),
  isRequired: false,
  isDisabled: false,
  label: '',
  initialValue: undefined,
  types: undefined,
  placeholder: 'Rechercher...',
  isWorld: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: Address | null): void
}>()

// AUTOCOMPLETE

const search = ref('')
const token = ref('')

const isLoading = ref(false)
const suggestions: Ref<SearchBoxSuggestion[]> = ref([])

watchDebounced(search, async () => {
  if (search.value.length <= 2) return

  isLoading.value = true

  const response = await placeAutocompleteAPI(search.value, {
    types: ['city'],
    sessionToken: token.value,
  })

  if (response.ok) {
    suggestions.value = response.data.suggestions ?? []
    if (response.data.sessionToken) token.value = response.data.sessionToken
  }

  isLoading.value = false
}, { debounce: 300, immediate: true })

// FOCUS

const isPopinActive = ref(false)

const $input = ref()
const $popinInput = ref()

watch(isPopinActive, () => {
  if (isPopinActive.value) {
    setTimeout(() => {
      if ($popinInput.value) $popinInput.value.focus()
    })
  }
})

const onSelect = async (suggestion: SearchBoxSuggestion) => {
  const response = await placeAutocompleteGetAPI(suggestion, {
    sessionToken: token.value,
    isAddress: true
  })
  
  suggestions.value = []
  search.value = ''

  if (response.ok && response.data.address) {
    emit('update:modelValue', response.data.address)
    if (response.data.sessionToken) token.value = response.data.sessionToken
  }

  isPopinActive.value = false
}

// OTHER

const onReset = () => {
  emit('update:modelValue', null)

  suggestions.value = []
  search.value = ''

  setTimeout(() => {
    if ($input.value) $input.value.focus()
  })
}

// META

onBeforeUnmount(() => onReset())

</script>
